import React from 'react';
import { DEFAULT_MEMBERSHIP_TYPE_OPTIONS, DEFAULT_PLAYER_TYPE_OPTIONS } from '../constants/membership';
import { isNonMemberGuestMembershipType, normalizeMembershipTypePricing } from '../utils/membership';
import { typesWithoutStartYear } from '../utils/playerTypes';

function MembershipTypeOptions({ clubEditForm, customMembershipOption, customPlayerOption, membershipOptionPick, membershipTypePricing, playerOptionPick, setClubEditForm, setCustomMembershipOption, setCustomPlayerOption, setMembershipOptionPick, setPlayerOptionPick, showToast, users }) {
  return (
    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
    <div>
      <label className="w3-tiny w3-text-grey"><i className="fa fa-id-card w3-margin-right"></i>Membership Type Options</label>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
        <select className="w3-select w3-border w3-round w3-small" style={{ minWidth: '220px', maxWidth: '320px' }}
          value={membershipOptionPick}
          onChange={e => setMembershipOptionPick(e.target.value)}>
          {[...new Set([...(clubEditForm.membershipTypeOptions || []), ...DEFAULT_MEMBERSHIP_TYPE_OPTIONS])].map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <button className="w3-button w3-tiny w3-theme-l4 w3-round" type="button"
          onClick={() => {
            if (isNonMemberGuestMembershipType(membershipOptionPick)) {
              showToast('Non-Member Guest is system-managed and cannot be added to membership type options', 'amber');
              return;
            }
            setClubEditForm(f => ({
              ...f,
              membershipTypeOptions: [...new Set([...(f.membershipTypeOptions || []), membershipOptionPick])],
              membershipTypePricing: {
                ...normalizeMembershipTypePricing(f.membershipTypePricing, f.membershipTypeOptions || []),
                [membershipOptionPick]: normalizeMembershipTypePricing(f.membershipTypePricing, f.membershipTypeOptions || [])[membershipOptionPick] ?? '',
              },
            }));
          }}>
          Add from list
        </button>
        <input className="w3-input w3-border w3-round w3-small" style={{ minWidth: '220px', maxWidth: '320px' }}
          placeholder="Add custom membership type"
          value={customMembershipOption}
          onChange={e => setCustomMembershipOption(e.target.value)} />
        <button className="w3-button w3-tiny w3-theme-l4 w3-round" type="button"
          onClick={() => {
            const v = customMembershipOption.trim();
            if (!v) return;
            if (isNonMemberGuestMembershipType(v)) {
              showToast('Non-Member Guest is system-managed and cannot be added to membership type options', 'amber');
              return;
            }
            setClubEditForm(f => ({
              ...f,
              membershipTypeOptions: [...new Set([...(f.membershipTypeOptions || []), v])],
              membershipTypePricing: {
                ...normalizeMembershipTypePricing(f.membershipTypePricing, f.membershipTypeOptions || []),
                [v]: normalizeMembershipTypePricing(f.membershipTypePricing, f.membershipTypeOptions || [])[v] ?? '',
              },
            }));
            setCustomMembershipOption('');
          }}>
          Add custom
        </button>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
        {(clubEditForm.membershipTypeOptions || []).map(opt => (
          <span key={opt} className="w3-tag w3-round w3-theme-l5" style={{ paddingRight: '6px' }}>
            {opt}
            <button type="button" className="w3-button w3-tiny w3-round w3-hover-red" style={{ padding: '0 4px', marginLeft: '6px' }} title={`Remove ${opt}`} aria-label={`Remove ${opt}`}
              onClick={() => setClubEditForm(f => {
                const nextOptions = (f.membershipTypeOptions || []).filter(x => x !== opt);
                const nextPricing = normalizeMembershipTypePricing(f.membershipTypePricing, nextOptions);
                return { ...f, membershipTypeOptions: nextOptions, membershipTypePricing: nextPricing };
              })}>
              <i className="fa fa-times"></i>
            </button>
          </span>
        ))}
      </div>

      <div className="w3-border w3-round" style={{ marginTop: '10px', padding: '10px', background: '#fff' }}>
        <div className="w3-small w3-text-grey" style={{ fontWeight: 700, marginBottom: '6px' }}>
          Pricing and details by membership type
        </div>
        <div className="w3-small w3-text-grey" style={{ marginBottom: '8px' }}>
          The yearly fee for each type, and what it covers. Both show on the membership form when someone
          picks that type. Leave either blank if you don't want to show it.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
          {(clubEditForm.membershipTypeOptions || []).map(opt => (
            <div key={`price-${opt}`} className="membership-type-detail">
              <div className="membership-type-detail-name">{opt}</div>
              <label className="w3-tiny w3-text-grey" htmlFor={`mt-price-${opt}`}>Yearly fee</label>
              <input
                id={`mt-price-${opt}`}
                className="w3-input w3-border w3-round w3-small"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={membershipTypePricing[opt] ?? ''}
                onChange={e => setClubEditForm(f => {
                  const nextPricing = { ...normalizeMembershipTypePricing(f.membershipTypePricing, f.membershipTypeOptions || []) };
                  const raw = e.target.value;
                  if (raw === '') {
                    delete nextPricing[opt];
                  } else {
                    const parsed = Number(raw);
                    if (Number.isFinite(parsed) && parsed >= 0) nextPricing[opt] = Number(parsed.toFixed(2));
                  }
                  return { ...f, membershipTypePricing: nextPricing };
                })}
              />
              <label className="w3-tiny w3-text-grey" htmlFor={`mt-desc-${opt}`} style={{ display: 'block', marginTop: '6px' }}>What it covers</label>
              <textarea
                id={`mt-desc-${opt}`}
                className="w3-input w3-border w3-round w3-small"
                rows={3}
                maxLength={800}
                placeholder="e.g. Full playing rights, pennants and club competitions, voting at the AGM."
                value={(clubEditForm.membershipTypeDescriptions || {})[opt] || ''}
                onChange={e => {
                  const text = e.target.value;
                  setClubEditForm(f => ({
                    ...f,
                    membershipTypeDescriptions: { ...(f.membershipTypeDescriptions || {}), [opt]: text },
                  }));
                }}
                style={{ resize: 'vertical' }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>

    <div>
      <label className="w3-tiny w3-text-grey"><i className="fa fa-users w3-margin-right"></i>Player Type Options</label>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
        <select className="w3-select w3-border w3-round w3-small" style={{ minWidth: '220px', maxWidth: '320px' }}
          value={playerOptionPick}
          onChange={e => setPlayerOptionPick(e.target.value)}>
          {[...new Set([...(clubEditForm.playerTypeOptions || []), ...DEFAULT_PLAYER_TYPE_OPTIONS])].map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <button className="w3-button w3-tiny w3-theme-l4 w3-round" type="button"
          onClick={() => setClubEditForm(f => ({
            ...f,
            playerTypeOptions: [...new Set([...(f.playerTypeOptions || []), playerOptionPick])],
          }))}>
          Add from list
        </button>
        <input className="w3-input w3-border w3-round w3-small" style={{ minWidth: '220px', maxWidth: '320px' }}
          placeholder="Add custom player type"
          value={customPlayerOption}
          onChange={e => setCustomPlayerOption(e.target.value)} />
        <button className="w3-button w3-tiny w3-theme-l4 w3-round" type="button"
          onClick={() => {
            const v = customPlayerOption.trim();
            if (!v) return;
            setClubEditForm(f => ({
              ...f,
              playerTypeOptions: [...new Set([...(f.playerTypeOptions || []), v])],
            }));
            setCustomPlayerOption('');
          }}>
          Add custom
        </button>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
        {(clubEditForm.playerTypeOptions || []).map(opt => (
          <span key={opt} className="w3-tag w3-round w3-theme-l5" style={{ paddingRight: '6px' }}>
            {opt}
            <button type="button" className="w3-button w3-tiny w3-round w3-hover-red" style={{ padding: '0 4px', marginLeft: '6px' }} title={`Remove ${opt}`} aria-label={`Remove ${opt}`}
              onClick={() => setClubEditForm(f => ({ ...f, playerTypeOptions: (f.playerTypeOptions || []).filter(x => x !== opt) }))}>
              <i className="fa fa-times"></i>
            </button>
          </span>
        ))}
      </div>

      {/* Per player type: does the form ask when they started bowling? A
          social member may never have. Unticked, the question is not shown
          and not required for someone who picks that type. */}
      {(clubEditForm.playerTypeOptions || []).length > 0 && (
        <div className="w3-border w3-round" style={{ marginTop: '10px', padding: '10px', background: '#fff' }}>
          <div className="w3-small w3-text-grey" style={{ fontWeight: 700, marginBottom: '6px' }}>
            Ask "Bowling Start Year"
          </div>
          <div className="w3-small w3-text-grey" style={{ marginBottom: '8px' }}>
            Untick a player type to leave the question off the membership form for anyone who picks it.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px' }}>
            {(clubEditForm.playerTypeOptions || []).map(opt => {
              const without = typesWithoutStartYear(clubEditForm.playerTypesWithoutStartYear, clubEditForm.playerTypeOptions);
              const asks = !without.includes(opt);
              return (
                <label key={`sy-${opt}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minHeight: '36px' }}>
                  <input type="checkbox" className="w3-check" style={{ top: 0, width: 18, height: 18 }}
                    checked={asks}
                    onChange={e => setClubEditForm(f => {
                      const current = typesWithoutStartYear(f.playerTypesWithoutStartYear, f.playerTypeOptions);
                      const next = e.target.checked ? current.filter(x => x !== opt) : [...new Set([...current, opt])];
                      return { ...f, playerTypesWithoutStartYear: next };
                    })} />
                  <span>{opt}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default MembershipTypeOptions;
